import { cn } from '../../lib/cn.ts';

export type PillTone = 'neutral' | 'accent' | 'success' | 'alert';

// Mirrors the prototype `.pill` family: a tinted wash background + matching ink,
// not a flat grey chip. `neutral` uses the sunken surface; the rest use their
// status wash (ok/warn/accent), exactly as `.pill.ok/.warn/.acc`.
const toneClass: Record<PillTone, string> = {
  neutral: 'bg-[var(--color-surface-sunken)] text-[var(--color-stone-700)]',
  accent: 'bg-[var(--color-accent-wash)] text-[var(--color-accent-deep)]',
  success: 'bg-[var(--color-ok-wash)] text-[var(--color-success)]',
  alert: 'bg-[var(--color-warn-wash)] text-[var(--color-alert)]',
};

export interface StatusPillProps {
  label: string;
  tone?: PillTone;
  /** Show the prototype's leading status dot (`.pill .dt`). */
  dot?: boolean;
  className?: string;
  'data-testid'?: string;
}

/**
 * StatusPill — a quiet status chip matching the prototype `.pill`: 11.5px medium
 * text, 3px·9px padding, fully-rounded, a tinted wash per tone, and an optional
 * leading dot in `currentColor`. The meaning is the text, never the colour alone.
 */
export function StatusPill({
  label,
  tone = 'neutral',
  dot = false,
  className,
  'data-testid': testId,
}: StatusPillProps) {
  return (
    <span
      data-testid={testId}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-[9px] py-[3px] text-[11.5px] font-medium tracking-[0.01em] whitespace-nowrap',
        toneClass[tone],
        className,
      )}
    >
      {dot && <span aria-hidden="true" className="size-[5px] shrink-0 rounded-full bg-current" />}
      {label}
    </span>
  );
}
